'use client'

import { BuilderBlocks } from '@builder.io/react'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { useEffect, useState } from 'react'

import { useResize } from '@src/utils/resize.util'
import { mouseWheelPlugin } from '@src/utils/slider.utils'
import { cn } from '@src/utils/tailwind'

export interface ReviewsProps {
  builderBlock?: { id: string }
  slots: Array<{ name: string; content: React.ReactNode; width: number }>
}

export function Reviews({ builderBlock, slots = [] }: ReviewsProps) {
  const resize = useResize() // re-render on window resize
  const [initialSize] = useState(resize)

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: false,
      mode: 'free',
      rubberband: false,
      slides: {
        perView: 'auto',
        origin: 'auto',
        spacing: 0,
      },
    },
    [mouseWheelPlugin]
  )

  // center the slides when the container width is larger than the slider width
  const [isReady, setIsReady] = useState(false)
  const [centerPadding, setCenterPadding] = useState(0)
  useEffect(() => {
    function getCenterPadding() {
      if (!instanceRef.current) return 0
      setIsReady(true)
      const { container, track } = instanceRef.current
      if (!track.details || track.details.length) return 0
      return (container.clientWidth * (1 - track.details.slidesLength)) / 2
    }
    setCenterPadding(getCenterPadding())
  }, [resize, instanceRef])

  return (
    <div className="bg-bodi-black-text text-white">
      <div ref={sliderRef} className="keen-slider">
        {slots?.length === 0 && (
          <div className="keen-slider__slide">
            <div className="flex h-[200px] flex-col items-center justify-center">
              <h1 className="text-body-md">Add some reviews</h1>
            </div>
          </div>
        )}
        {slots?.map(({ content, width = 250 }, index) => (
          <div
            key={index}
            className={cn([
              'keen-slider__slide',
              'box-content flex flex-col items-center justify-center',
              'border-x-[25px] border-y-[38px] border-transparent lg:border-x-[50px]',
              initialSize.width === resize.width ? 'transition-opacity' : 'transition-all',
            ])}
            style={{
              width,
              minWidth: width,
              maxWidth: width,
              marginLeft: index === 0 ? centerPadding : undefined,
              opacity: isReady ? 1 : 0,
            }}
          >
            <BuilderBlocks
              className="flex w-full flex-col items-center justify-center"
              parentElementId={builderBlock?.id}
              dataPath={`component.options.slots.${index}.content`}
              blocks={content}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
